import type moment from 'moment';

import type { SORTING_ORDER } from '@/contants/common';
import type { SHIPMENT_FOOTER_TYPES } from '@/contants/ShipmentEnums';

import type { SpotBidAccessorial } from '../AuctionScreens/types';
import type { LengthUOM, LengthUOMDescription, PortType, WeightUOM, WeightUOMDescription } from '../common/types';
import type { ShipmentRateShopDetails } from '../RatesShop/types';
import type { VendorAddressAppointment } from '../VendorAddress/types';
import type { PRINTED_BOL_FLAG_TYPES, SHIPMENT_LOCATION_STOP, SHIPMENT_STATUS } from './constants';

export const SHIPMENT_TYPES = {
  '3': { label: 'Third Party', value: '3' },
  I: { label: 'Inbound', value: 'I' },
  N: { label: 'Intercompany', value: 'N' },
  O: { label: 'Outbound', value: 'O' },
  R: { label: 'Return', value: 'R' },
} as const;
export type ShipmentType = keyof typeof SHIPMENT_TYPES;
export type ShipmentTypeDescription = (typeof SHIPMENT_TYPES)[keyof typeof SHIPMENT_TYPES]['label'];

export type ShipmentStatus = (typeof SHIPMENT_STATUS)[keyof typeof SHIPMENT_STATUS];

export type PalletLoadType = 'Stackable' | 'Non Stackable';
export type ShipmentLocationStopType = (typeof SHIPMENT_LOCATION_STOP)[keyof typeof SHIPMENT_LOCATION_STOP];
export type AppointmentType = 'Appointment Required'; // TODO: connect with VendorAddressAppointment

export type ShipmentSummary = {
  gId: number;
  class: string | null;
  classCode: string | null;
  cube: number | null;
  height: number | null;
  length: number | null;
  totalWeight: number | null;
  unitMeasure: LengthUOMDescription | null; // deprecated?
  unitMeasureCode: LengthUOM | null;
  weightUom: 'Pound' | 'Kilogram' | 'OUNCE' | null; // deprecated?
  weightUomCode: WeightUOM | null;
  packageUom: string | null; //deprecated?
  packageUomCode: string | null;
  puomTotalQuantity: number | null;
  packageType: PalletLoadType;
  packageTypeCode: PalletLoadType | null; // deprecated?
  codeId: string | null;
  codeDesc: string | null; // deprecated?
  equipmentKey: number;
  equipmentDesc: string | null; // deprecated?
  width: number | null;
  crateNo: string | null;
  isShowCrate: boolean;
  crateNoNum: string | null;
  isHTSCode: null | 'Y';
  isECCN: null | 'Y';
  isCatchDate: null | 'Y';
  htsCode: string | null; // deprecated?
  eccn: string | null; // deprecated?
  catchDate: string | null; // deprecated?
};

export type ShipmentBillOfLadingNumber = {
  id?: number;
  billOfLadingNumber: string;
  containerNumber: string;
  pickUpReference: string;
  specialInstruction: string;
  invoiceNumber: string;
};

export type ShipmentLocationDetailsBase = {
  address1: string | null;
  address2: string | null;
  id: number;
  businessPhone: string;
  city: string;
  searchId: string;
  name: string;
  latitude: string | null; // "41.5213540"
  longitude: string | null; // "41.5213540"
  zipCode: string | null;
  stateCode: string | null;
  countryCode: string;
  portName: string | null;
  portType: PortType | string | null;
  portCode: string | null;
  portRecKey: number | null;
  weeklyOperation: string | null; // Sunday TO Friday
  hoursOperation: string | null; //  11:00 AM TO 06:00 PM
  trailerNumber?: string;
  displaySequence: number;
  gridOperation: number; // 0 - no action, 1 - create, 2 - update, 3 - remove
  extension?: string | null;
  dockNumber?: string;

  // Candidates for removal
  shipmentKey: number;
  legNumber: number | null;
  sequence: number; // we already have displaySequence
  matchedBolKey: null;
  countryName: string; // we already have country code
  stateName: string | null; // we already have state code
  hrsOfOperation: string; // Sunday TO Friday | 11:00 AM TO 06:00 PM, see hoursOperation and weeklyOperation
  legMiles?: number;
  customerKey: number;
  oldStopSequence: number; // deprecated
  createdBy: number;
};

export type ShipmentLocationDetailsShipper = ShipmentLocationDetailsBase & {
  shipperReferenceNo: string;
  shipperKey: number;
  stopType: (typeof SHIPMENT_LOCATION_STOP)['SHIPPER'] | null;
  shipperRequestedShipDate: string; // '2025-03-19T02:30:00'
  originAdditionalInfo: ShipmentLocationAdditionalDetails | null;
  shipperLocationId: string | null;

  // To remove
  shipperContactName: string | null; // already present in additional details
  type: null; // not used + always null
  pickupDateTime: null; // not used + always null
  shipperDetails: null; // not used + always null
  shipperAppointmentType: AppointmentType | '' | null; // already have appointmentType in additional details
  shipperBusinessPhone: string | null; // already have businessPhone field
};

export type VendorShipmentLocationDetailsShipper = Omit<
  ShipmentLocationDetailsShipper,
  'shipperRequestedShipDate' | 'pickupDateTime' | 'type'
> & {
  shipRecKey: number;
  vendorAddressKey: null;
  vendorKey: number | null;
  status: string;
  originFlag: 'y' | 'n';
  addr1: string;
  addr2: string | null;
  state: string;
  stateDesc: string;
  zip: string;
  effectiveDate: string; // '2023-09-12T12:08:40.037'
  contactName: string;
  email: string;
  sourceType: VendorAddressAppointment;
  createdOn: string; // '2023-09-12T12:08:40.037'
};

export type VendorShipmentLocationDetailsConsignee = Omit<
  ShipmentLocationDetailsConsignee,
  'shipperRequestedDeliveryDate' | 'totalWeight' | 'totalPallet' | 'deliveryDateTime'
> & {
  shipRecKey: number;
  status: string;
  consigneeFlag: 'y' | 'n';
  addr1: string;
  addr2: string | null;
  state: string;
  stateDesc: string;
  zip: string;
  effectiveDate: string; // '2023-09-12T12:08:40.037'
  contactName: string;
  email: string;
  createdOn: string; // '2023-09-12T12:08:40.037'
  hoursOfOperation: string | null;
  appointmentType: string;
};

export type ShipmentLocationDetailsConsignee = ShipmentLocationDetailsBase & {
  consigneeReferenceNo: string | null;
  stopType: (typeof SHIPMENT_LOCATION_STOP)['CONSIGNEE'] | null;
  shipperRequestedDeliveryDate: string; // '2025-03-19T02:30:00',
  destinationAdditionalInfo: ShipmentLocationAdditionalDetails | null;
  consigneeLocationId: string | null;

  // Candidates for removal
  consigneeContactName: string | null; // already present in additional details
  consignee: null;
  totalWeight: null;
  totalPallet: null;
  deliveryDateTime: null;
  consigneeBusinessPhone: string | null; // already have businessPhone field
  consigneeKey: number; // already have id?
  consigneeAppointmentType: AppointmentType | '' | null; // already have appointmentType in additional details
};

export type ShipmentLocationAdditionalDetails = {
  shipperRequestedShipDate: string | null; // '2025-03-19T02:30:00'
  carrierAppontmentShipDate: string | null;
  carrierActualShipDate: string | null;
  shipperRequestedDeliveryDate: string | null;
  carrierAppontmentDeliveryDate: string | null;
  carrierActualDeliveryDate: string | null;
  contactEmail: string;
  appointmentType: VendorAddressAppointment | string; // TODO: move type to common?
  taxId: string;
  isModifiedDateTime: number;
  contactName: string | null;

  // Candidate for removal
  hoursOfOperation: string; // already present in parent level
  createdBy: number;
  sequence: number; // already present in parent level
  shipmentKey: number;
  uom: string;
  legMiles: number;
  gridOperation: number; // already present in parent level
  displaySequence: number; // already present in parent level
  pickupDeliveryID: string | null; // same as *ReferenceNo in parent level
  phoneExt: null; // already present in parent level + always null
  phone: string; // already present in parent level
  doorDockID: string | null; // unknown
  dockNumber: null; // already present in parent level + always null
};

export type ShipmentDocument = {
  customerKey: number;
  shipmentKey: number;
  documentTitle: string | null;
  documentTypeCode: string;
  documentSequence: number;
  description: string | null;
  documentFileName: string;
  originalFileName: string;
  documentDesc: string;
  createdBy: number;
  filePath: string;
  userType: string | null;
  referenceNo: string;
  version: null;
  carrierKey: number | null;
  carrierName: string;
  cloudUrl: null;
  isVendorVisible: boolean | null;
  isCarrierVisible: boolean | null;
};

// Common Shipment fields
export type ShipmentBase = {
  shipmentNo: string; // '11490151'
  shipmentType: ShipmentType;
  status: ShipmentStatus;
  customerKey: number | null;
  shipmentKey: number; // 1782799
};

// Shipment item on shipment search for Customer
export type ShipmentListItemCustomerApi = ShipmentBase & {
  poNumber: string | null; // 'PO145114'
  totalUnits: number | null;
  totalCube: number | null;
  totalWeight: number | null;
  shipmentDate: string; // '2025-03-19T02:30:00'

  weightUom: WeightUOMDescription;
  rateShopCost: number | string; // 0, '0.00', 330.9
  proNo: string | null; // '771056299911'
  bolNumber: string | null;
  expShipDate: string | null; // '2025-03-19T02:30:00'
  expDeliveryDate: string | null; // '2025-03-19T02:30:00'
  cargoType: string;
  consignee: string | null; // 'New York, NY, 00501, US'
  carrierName: string | null;
  createdOn: string; // '2025-03-18T06:44:39.66'
  createdBy: string;
  bolPrinted: keyof typeof PRINTED_BOL_FLAG_TYPES | null;
  masterShipmentNo: string;

  uom: string | null; // 'Bag'
  multiStop: number;
  totalMileage: number;
  approvalNumber: string | null;
  masterBolNumber: string;
  containerNumber: string | null;

  quoteNo: string;
  projectNo: string | null;
  // Shipper
  shipperName: string | null;
  shipperCountryCode: string | null;
  shipperZip: string | null;
  shipperState: string | null;
  shipperCity: string | null;
  // Consignee
  consigneeName: string | null;
  countryCode: string;
  consigneeZip: string | null;
  consigneeState: string | null;
  consigneeCity: string | null;

  poNumbers: string;
  soNumber: string;
  partNumber: string | null;
  shipmentTerms: string;
  shipmentTermsDesc: string;
  expBidTime: string;
  isMasterShipment: string; // '', 'N', 'Y'
  isChildShipment: string; // '', 'N', 'Y'
  incoTerms: string;
  shipmentOriginator: string;
  shipmentCost: number | null;

  // unique fields
  weightUtilization: null;
  volumeUtilization: null;
  convertedCurrency: string;
  carrierScacCode: string;

  // TODO: Candidates for removal – they shouldn't be here. Ask back-end team
  remiangHr: 0;
  remiangMin: 0;
  remiangSec: 0;
  isDateTimeZone: string;
  shipmentCount: 0;
  latepickup: 0;
  latedelivery: 0;
  trackingfailures: 0;
  pickupFailed: 0;
  missingDocument: 0;
  shipperPortType: PortType;
  listLatitudeLongitude: null;
  shipper: string; // 'TMS Pees,test TMS,08,45241,MY'
  delayed: 0 | 1;
  stdTransitDay: 0;
  consigneeTrailerNumber: string;
  partDescription: string;
  consigneePortType: PortType;
  bolKey: null;
  customerName: string | null;
  detailNotes: string;
  shipmentNotes: string;
  printBol: string | null;
  fullName: string;
  userEmail: string;
  modifiedOn: string | null; // '2024-12-27T08:02:18.337'
  modifiedBy: number;
  modifiedFullName: null;
  palletLoadType: PalletLoadType;
  carrierRequirements: string | null;
  actualShipDate: null;
};

// Shipment detailed info for customer
export type ShipmentDetailedItemCustomerApi = ShipmentBase & {
  poNumber: string | null; // 'PO145114'
  carrierRequirements: string | null;
  totalUnits: number | null;
  totalCube: number | null;
  totalWeight: number | null;
  weightUOM: WeightUOM;
  palletLoadType: PalletLoadType;
  customerName: string;
  originKey: number | null;
  consigneeKey: number | null;
  createdOn: string; // '2025-03-18T06:44:39.66'
  branchKey: number;
  accountNumber: null;
  fobCode: string | null;
  freightAllowance: string | null;
  shipmentTerms: string;
  requiredCarrierKey: number;
  requiredCarrierName: string | null;
  shipmentCost: number;
  proNo: string | null;
  bolNumber: string;
  masterBolNumber: string;
  carrierKey: number;
  tenderEmailAddress: string;
  carrierName: string | null;
  isAdhocCarrier: boolean;
  adhocCarrierName: string;
  specialServices: string;
  trailerNumber: string | null;
  numberOfStops: null;
  stopSequence: null;
  signedForBy: null;
  vendorKey: number;
  loadNumber: null;
  scenarioId: '-1' | null;
  apptTimeStartHours: null;
  apptTimeStartMin: null;
  apptTimeStopHours: null;
  apptTimeStopMin: null;
  apptStartDate: null;
  apptStopDate: null;
  roundTrip: null;
  optimizationDate: null;
  liabilityExceededFlag: null;
  originalStatus: null;
  aggregationNum: null;
  altOrigin: null;
  altDestination: null;
  apptStartDatetime: null;
  apptStopDatetime: null;
  printBol: null;
  rateShopCost: number;
  pricingBasis: null;
  cargoType: string;
  prepaidCollect: null;
  bolKey: null;
  trackingStatus: null;
  trackingDatetime: string; // '2025-03-17 08:23:05.993'
  exceptionNumber: null;
  exceptionDesc: null;
  baseCost: null;
  baselineCost: null;
  baselinePricingDetailKey: null;
  optimizedCost: null;
  optimizedPricingDetailKey: null;
  ratingErrorMsg: null;
  customerNumber: string;
  pricingType: null;
  pickupDate: null;
  pickupReadyHour: null;
  pickupReadyToHour: null;
  pickupReadyAmpm: null;
  pickupReadyToAmpm: null;
  shipperReferenceNo: null;
  hazmatContactName: null;
  hazmatContactNumber: null;
  codAmount: null;
  deliveryInstructions: null;
  pickupInstructions: null;
  dispatchNotes: null;
  deliveryReadyHour: null;
  deliveryReadyAmpm: null;
  deliveryReadyToHour: null;
  deliveryReadyToAmpm: null;
  shipmentNotes: string;
  serviceKey: null;
  incoCode: string;
  incoTerms: string;
  incoPoint: string;
  shipmentRoutingMethod: string;
  bolPrinted: null;
  detailNotes: string;
  currencyCode: string;
  specialInstructions: string;
  paidAmount: null;
  paymentTerms: string;
  standardTransitDays: null;
  trailerKey: number;
  employeeId: null;
  convertedCurrencyCode: string;
  convertedCost: number;
  masterShipmentNo: string;
  isMasterShipment: string;
  isCompleteMstChld: 'Y' | 'N';
  isChildShipment: string;
  masterShipmentTotalWeight: number;
  masterShipmentCurrentWeight: number;
  masterShipmentTotalQty: number;
  masterShipmentCurrentQty: number;
  restChildQty: null;
  restChildWeight: number;
  createdBy: number;
  modifiedBy: number;
  customerContact: string;
  transportationMode: null;
  originPortKey: number;
  destinationPortKey: number;
  rateShopKey: number;
  shipperName: string;
  shipperAddr1: string;
  shipperAddr2: string | null;
  shipperCity: string;
  shipperState: string;
  shipperZip: string;
  shipperCountry: string;
  consigneeName: string;
  consigneeAddr1: string;
  consigneeAddr2: string | null;
  consigneeCity: string;
  consigneeState: string;
  consigneeZip: string;
  consigneeCountry: string;
  contractedShipmentCost?: number;
  contractedCurrencyCode?: string;
  contractedCurrencyName?: string;
  shipmentOriginDetails: ShipmentLocationDetailsShipper[];
  shipmentDestinationDetails: ShipmentLocationDetailsConsignee[];
  shipmentBillToInfo: null;
  shipmentVendorDetails: [];
  rateShopDetails: ShipmentRateShopDetails[];
  hierarchialDetails: [];
  shipmentDocuments: ShipmentDocument[];
  shipmentAccessorials: [];
  shipmentTaxes: [];
  createdByName: string;
  modifiedByName: string;
  modifiedOn: string | null;
  action: number;
  saveStatus: string;
  approvalNumber: string;
  totalMileage: number;
  projectNo: null;
  quoteNo: string;
  shipmentTotalCube: number;
  shipmentTotalWeight: number;
  shipmentTotalQty: number;
  billQtyUOM: string;
  hazmat: number;
  purchaseOrderNumber: string;
  salesOrderNumber: string;
  carrierShortName: string;
  rateType: string; // Rated
  trackingNumber: string;
  trailerDescription: string | null;
  shipmentTermsDesc: string;
  shipmentTypeDesc: ShipmentTypeDescription;
  branchDesc: string;
  billQtyUOMDesc: string;
  weightUOMDesc: null;
  cargoTypeDesc: string;
  serviceLevel: string;
  pickupConfirmationNumber: string;
  shipmentOriginator: string;
  trailerTemperature: string;
  udfs?: ShipmentUDFs;
  shipmentSpotBidAccessorials?: SpotBidAccessorial[] | null;
};

export type ShipmentUDFs = {
  tempControl?: 'Y' | 'N' | null;
  routingMovementType?: string | null;
  refDryIndicator?: string | null;
  laneIDReference?: string | null;
  controlDrug?: 'Y' | 'N' | null;
  TrailerReference?: string | null;
};

export type ShipmentListItemTrackingApi = Omit<ShipmentBase, 'shipmentType' | 'shipmentKey'> & {
  shipmentkey: number; // TODO: ask back-end to rename it to shipmentKey for consistency
  colorFlag: number;
  shipmentType: ShipmentType;

  // unique fields
  transportationProvider: null;
  shipperReferenceNumber: string | null;
  color: null;
  shipmentMode: string | null;
  delivery: null;
  expectedDeliveryDate: string | null;
  actualDeliveryDate: string | null;
  shipReqShipDate: string;
  shipReqShipDateDt: string | null;
  shipReqDeliveryDate: string;
  transProAcShipDate: string;
  transProAcDeliveryDate: string;
  statusCode: null;
  locationCity: null;
  locationCountry: string;
  locationCountryName: string;
  locationState: string;
  locationStateName: string;
  signature: null;
  shipmentCurrentStatus: ShipmentStatus;
  shipmentStatusType: string;
  shipmentStatusTypeName: string;
  shipmentExceptionType: string;
  shipmentExceptionTypeName: string;
  message: null;
  image: null;
  shipperStateCode: null;
  isDocument: number;
  clientDateTime: null;
  timeAtStatus: null;
  dateTimeStamp: null;
  shipTime: string;
  delivaryTime: string;
  eventLocalTime: null;
  gridTimeAtStatus: null;
  gridActualShipDate: null;
  gridActualDeliveryDate: null;
  createdOnDate: string; // '2025-02-17T09:07:08.94'
  businessSegment: string;
  orderQty: number;
  individualItemQty: null;
  appointmentNumber: null;
  carrierKey: number;
  customerName: string;
  customerShipmentNumber: string | null;
  shipperName: string;
  originCity: string;
  originCountryCode: string;
  originCountry: string;
  originPostal: string;
  originStateCode: string;
  originState: string;
  consigneeName: string | null;
  consigneeReferenceNumber: number | string | null;
  destinationCity: string | null;
  destinationCountryCode: string;
  destinationCountry: string | null;
  destinationCountryName: string | null;
  destinationPostal: string | null;
  destinationStateCode: string;
  destinationState: string | null;
  expectedShipDate: string | null;
  actualShipDate: string | null;
  trackingStatus: string; // i.e. New
  statusDate: null;
  shipperCountryCode: null;
  conStateCode: null;
  conCountryCode: null;
  multiStop: number;
  branchDesc: string;
  shipmentCost: number | null;
  rateShopCost: string;
  currencyCode: string | null;
  purchaseOrderNumber: string | null;
  salesOrderNumber: string | null;
  partNumber: null;
  partNumberDescription: null;
  partSerialNumber: string | null;
  containerNumber: null;
  bolNumber: string | null;
  dockNumber: null;
  shipperDockNo: string | null;
  consingneeDockNo: string | null;
  lineNumber: null;
  billQty: number;
  billQtyUomDesc: null;
  itemQty: number;
  unitMeasure: null;
  scenarioId: null;
  clienTimeZone: null;
};

export type ShipmentDetailedItemTrackingApi = ShipmentBase & {
  shipmentDate: string; // '2025-03-19T02:30:00';
  poNumber: string | null; // 'PO145114'
  carrierRequirements: string | null;
  totalUnits: number | null;
  totalCube: number | null;
  totalWeight: number | null;
  weightUOM: WeightUOM;
  palletLoadType: PalletLoadType | '';
  shipmentOriginDetails: ShipmentLocationDetailsShipper[];
  shipmentDestinationDetails: ShipmentLocationDetailsConsignee[];
  shipmentDocuments: ShipmentDocument[];
  hierarchialDetails: [];
  shipmentAccessorials: [];
  shipmentTaxes: [];
  rateShopDetails: ShipmentRateShopDetails[];
  customerName: string;
  originKey: null;
  consigneeKey: null;
  createdOn: string;
  branchKey: number;
  accountNumber: string | null;
  fobCode: null;
  freightAllowance: null;
  shipmentTerms: string;
  requiredCarrierKey: number;
  requiredCarrierName: string | null;
  shipmentCost: number;
  proNo: string;
  bolNumber: string;
  masterBolNumber: string;
  carrierKey: number;
  tenderEmailAddress: string;
  carrierName: string | null;
  isAdhocCarrier: false;
  adhocCarrierName: string;
  specialServices: null;
  trailerNumber: string | null;
  numberOfStops: null;
  stopSequence: null;
  signedForBy: null;
  vendorKey: null;
  loadNumber: null;
  scenarioId: '-1';
  apptTimeStartHours: null;
  apptTimeStartMin: null;
  apptTimeStopHours: null;
  apptTimeStopMin: null;
  apptStartDate: null;
  apptStopDate: null;
  roundTrip: null;
  optimizationDate: null;
  liabilityExceededFlag: null;
  originalStatus: null;
  aggregationNum: null;
  altOrigin: null;
  altDestination: null;
  apptStartDatetime: null;
  apptStopDatetime: null;
  printBol: null;
  rateShopCost: number;
  pricingBasis: string | null;
  cargoType: string;
  prepaidCollect: null;
  bolKey: null;
  trackingStatus: null;
  trackingDatetime: string; // '2024-12-27 08:02:18.337'
  exceptionNumber: null;
  exceptionDesc: null;
  baseCost: null;
  baselineCost: null;
  baselinePricingDetailKey: null;
  optimizedCost: null;
  optimizedPricingDetailKey: null;
  ratingErrorMsg: null;
  customerNumber: string;
  pricingType: null;
  pickupDate: null;
  pickupReadyHour: null;
  pickupReadyToHour: null;
  pickupReadyAmpm: null;
  pickupReadyToAmpm: null;
  shipperReferenceNo: null;
  hazmatContactName: null;
  hazmatContactNumber: null;
  codAmount: null;
  deliveryInstructions: null;
  pickupInstructions: null;
  dispatchNotes: null;
  deliveryReadyHour: null;
  deliveryReadyAmpm: null;
  deliveryReadyToHour: null;
  deliveryReadyToAmpm: null;
  shipmentNotes: string;
  serviceKey: number;
  incoCode: null;
  incoTerms: string;
  incoPoint: string;
  shipmentRoutingMethod: string;
  bolPrinted: null;
  detailNotes: string;
  currencyCode: string;
  specialInstructions: string;
  paidAmount: number;
  paymentTerms: string;
  standardTransitDays: null;
  trailerKey: number;
  employeeId: null;
  convertedCurrencyCode: string | null;
  convertedCost: number;
  masterShipmentNo: string;
  isMasterShipment: string;
  isCompleteMstChld: string;
  isChildShipment: string;
  masterShipmentTotalWeight: number;
  masterShipmentCurrentWeight: number;
  masterShipmentTotalQty: number;
  masterShipmentCurrentQty: number;
  restChildQty: null;
  restChildWeight: number;
  createdBy: number;
  modifiedBy: number;
  customerContact: string;
  transportationMode: null;
  originPortKey: number;
  destinationPortKey: number;
  rateShopKey: number;
  shipperName: string;
  shipperAddr1: string;
  shipperAddr2: string | null;
  shipperCity: string;
  shipperState: string;
  shipperZip: string;
  shipperCountry: string;
  consigneeName: string;
  consigneeAddr1: string;
  consigneeAddr2: string | null;
  consigneeCity: string;
  consigneeState: string;
  consigneeZip: string;
  consigneeCountry: string;
  shipmentBillToInfo: null;
  shipmentVendorDetails: [];
  createdByName: string;
  modifiedByName: string;
  modifiedOn: string; // '2024-12-27T08:02:18.337'
  action: number;
  saveStatus: string;
  approvalNumber: string;
  totalMileage: number;
  projectNo: null;
  quoteNo: string;
  shipmentTotalCube: number;
  shipmentTotalWeight: number;
  shipmentTotalQty: number;
  billQtyUOM: string;
  hazmat: number;
  purchaseOrderNumber: string;
  salesOrderNumber: string;
  carrierShortName: string;
  rateType: string;
  trackingNumber: string;
  shipmentTermsDesc: string;
  shipmentTypeDesc: ShipmentTypeDescription;
  branchDesc: string;
  billQtyUOMDesc: string;
  weightUOMDesc: null;
  cargoTypeDesc: string;
  serviceLevel: string;
  pickupConfirmationNumber: string;
  shipmentOriginator: string;
  trailerTemperature: string;
};

// Shipment item for Vendor
export type ShipmentListItemVendorApi = ShipmentBase & {
  shipmentDate: string; // '2025-03-19T02:30:00'
  carrierRequirements: string | null;
  // unique fields
  origin: string;
  distance: number;
  totalShipmentCount: null;
  consignee: string;
  cube: number;
  weight: number;
  billQty: number;
  branchKey: number;
  vendorKey: number;
  originKey: number;
  consigneeKey: number;
  customerName: string;
  carrierName: null;
  proNo: string;
  bolNumber: string;
  expShipDate: string; // '2023-09-12T00:00:00'
  expDeliveryDate: string; // '2023-01-09T00:00:00'
  fobCode: string;
  freightAllowance: string;
  shipmentTerms: string;
  shipmentNotes: string;
  rateShopCost: number;
  pricingBasis: string;
  cargoType: string;
  carrierKey: number;
  requiredCarrierKey: number;
  employeeId: string;
  incoCode: string;
  shipmentCost: number;
  createdBy: number;
  createdOn: string; // '2023-09-12T12:08:39.147'
  shipperName: null;
  shipperCity: null;
  shipperState: null;
  shipperZip: null;
  shipperCountry: null;
  consigneeName: null;
  consigneeCity: null;
  consigneeState: null;
  consigneeZip: null;
  consigneeCountry: null;
  uom: null;
  approvalNumber: string;
  preBookingStatus: string;

  // UI fields (TODO: to delete later during refactoring)
  isChecked?: boolean;
};

// Shipment item detailed info for Vendor
export type ShipmentDetailedItemVendorApi = ShipmentBase & {
  shipmentDate: string; // '2025-03-19T02:30:00'
  palletLoadType: PalletLoadType;
  othersDetails: ShipmentBillOfLadingNumber[];
  shipmentOriginDetails: VendorShipmentLocationDetailsShipper[];
  shipmentDestinationDetails: VendorShipmentLocationDetailsConsignee[];
  branchKey: number;
  vendorKey: number;
  proNo: string;
  bolNumber: string;
  expShipDate: string;
  expDeliveryDate: string;
  fobCode: null;
  shipmentTerms: string;
  shipmentNotes: string;
  cargoType: string;
  createdBy: number;
  createdOn: string; // '2023-09-12T12:08:39.147'
  equipmentDesc: null;
  trailerNumber: null;
  approvalNumber: null;
  preBookingStatus: string;

  // unique fields
  summeryDetails: ShipmentSummary[];
  filesDetails: []; // ???
  invoiceNo: string;
  originPortDetails: null;
  destinationPortDetails: null;
  shipmentDetails: {
    shipmentKey: number;
    itemSequenceKey: number;
    quantity: number;
    unitMeasure: string;
    productCode: string;
    productDescription: string;
    class: string;
    deliveryID: null;
    length: number;
    height: number;
    width: number;
    dimUom: string;
    weight: number;
    totalWeight: null;
    weightUom: WeightUOM;
    cube: number;
    totalCube: null;
    billQty: number;
    billQtyUom: string;
    createdBy: number;
    classDesc: null;
    unitMeasureDesc: null;
    weightUOMDesc: WeightUOMDescription;
    dimUOMDesc: null;
    billQtyUOMDesc: string;
    reference1: string;
    eccn: string;
    hts: string;
    countryOfOrigin: string;
    createdOn: string; // '2023-09-12T12:08:39.78'
    poNumber: string;
    poQuantity: null;
    quantityShipped: null;
    poQuantityLeft: null;
    poPaymentTerm: null;
    receivingDocument: null;
    customerKey: null;
    boNumber: null;
    branchKey: null;
    poID: number;
    poPrice: null;
    rowID: number;
    codeDesc: null;
    equipmentDesc: null;
    customerName: null;
    expShipDate: null;
    expDeliveryDate: null;
    shipmentNo: null;
    crateNo: string;
    countryName: null;
    catchDate: null;
    nmfcNumber: null;
    itemShipDate: null;
  }[];
};

export type ShipmentSearchFilters = {
  CreatedDateFrom: string | moment.Moment;
  CreatedDateTo: string | moment.Moment;
  PageNumber: number;
  PageSize: number;
  ShipmentTerms?: string;
  CargoType?: string;
  OrderBySort: SORTING_ORDER;
  OrderByColumn: string;
  QuoteNo?: string;
  ApprovalNumber?: string;
  ShipmentStatus?: ShipmentStatus;
  ShipmentNo?: string;
  ExpShipFrom?: string | moment.Moment;
  ExpShipTo?: string | moment.Moment;
  BranchKey?: number;
  CarrierKey?: number;
  PoNo?: number;
  SoNo?: number;
  BolNumber?: string;
  ProjectNo?: string | null;
  createdBy?: number;
  VendorKey?: number;
  OCountryCode?: string;
  ShipperName?: string;
  ShipperCity?: string;
  ShipperState?: string;
  ShipperZip?: string;
  CCountryCode?: string;
  ConsigneeName?: string;
  ConsigneeCity?: string;
  ConsigneeState?: string;
  ConsigneeZip?: string;
  IncoTerms?: string;
  shipmentOriginator?: string;
  modifiedBy?: number;
  SelectedValue?: (typeof SHIPMENT_FOOTER_TYPES)[keyof typeof SHIPMENT_FOOTER_TYPES] | '';
};

export type ShipmentAuditLog = {
  logTransID: number;
  database: string;
  tableName: string;
  tableSchema: string;
  auditActionID: 3;
  hostName: string;
  appName: string;
  modifiedBy: string;
  modifiedDate: string; // '07-28-2024 02:58:02'
  affectedRows: number;
  sysObjID: number;
  auditLogDetailsData: ShipmentAuditLogAdditionalInfo[];
};

export type ShipmentAuditLogAdditionalInfo = {
  logDataID: number;
  logTransID: number;
  key: string;
  tableName: string;
  tableSchema: string;
  columnName: string;
  oldValue: string;
  newValue: string;
  dataType: string;
  key1: string;
  key2: string;
  key3: string;
  key4: string;
};

export type ShipmentProductDetails = {
  itemSequenceKey: number;
  shipmentProductKey: number;
  shipmentDetailsID: string;
  gridOperation: number;
  shipmentKey: number;
  classFlag: number;
  quantity: number;
  unitMeasure: string;
  unitMeasureName: string;
  productCode: string;
  productDescription: string;
  class: string;
  className: string;
  deliveryID: string;
  length: number;
  height: number;
  width: number;
  dimUom: string;
  dimUomName: string;
  weight: number;
  actualWeight: number;
  weightUom: WeightUOM;
  weightUomName: WeightUOMDescription;
  cube: number;
  billQty: number;
  billQtyUom: string;
  billQtyUomName: string;
  createdBy: number | null;
  hazmat: string;
  hazmatUnNo: string;
  stcDescription: string;
  accessorialData: {
    qty: number | null;
    uom: string | null;
    uomDesc: string | null;
  } | null;
  soNumber: string;
  poNumber: string;
  reference1: string;
  palletLoadType: PalletLoadType;
  countryOfOrigin: string;
  countryOfOriginName: string;
  hts: string;
  eccn: string;
  crateNo: string;
  shipmentNo: string;
  cartonQty: number;
  cartonUOM: string;
  cartonUOMName: string;
  unitPrice: number;
  nmfcNumber: string;
  customerSeqNumber: string;
  bookOks: null;
  book: null;
  soft: null;
  softOks: null;
  misc: null;
  miscOKs: null;
  itemSerialNumber: string | null;
};

export type Carrier = {
  carrierKey: number;
  carrierShortName: string;
  carrierName: string;
  carrierScacCode: string;
  carrierAddr1: string;
  carrierAddr2: string | null;
  carrierCity: string;
  carrierState: string;
  carrierZip: string;
};

export type Vendor = {
  shipmentKey: number;
  vendorKey: number;
  customerKey: number;
  vendorName: string;
  vendorPhone: string;
  vendorEmail: null;
  city: string;
  stateCode: string;
  postalCode: string;
  countryCode: string;
  createdBy: number;
  countryName: string;
  stateName: string;
  gridOperation: number;
  id: number;
};

export type NonLCCApprover = {
  userId: number;
  loginId: string; // 'pinakim@tranistics.com'
  name: string;
  approvalPriority: number;
  shipmentKey: number;
  requestedBy: number;
  isChecked: number;
  notApprovalRequired: number;
};
